import React, { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { useDispatch } from "react-redux";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useToast } from "react-native-toast-notifications";
import {
  createFactoryRunShipment,
  createDirectFactoryRunShipment,
  deleteFactoryRunShipment,
  deleteFactoryOrderDetail,
  generateChinaShipment,
  updateGeneratedChinaShipment,
  getChinaShipments,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useTranslation } from "../../../utils/useTranslation";
import { DapiConvertResponse, DapiDocSplit, DapiShipmentItem } from "../../../services/dapiService";
import { matchShipmentItems } from "../../../services/chinaTrackerService";
import { fetchSignedLabelURL } from "../utils/imageKey";

interface Props {
  visible: boolean;
  data: DapiConvertResponse | null;
  // When set, this modal is replacing an already-generated shipment for this
  // container (rather than creating a new one) — the ChinaShipmentMain doc,
  // populated with its current ChinaShipmentDetail items.
  existingShipment?: any;
  // Set when this container is one of several in an upload batch, so the header can
  // show progress and the parent can advance to the next container on success.
  batchPosition?: { index: number; total: number };
  onSubmitted?: () => void;
  onClose: () => void;
}

interface Candidate {
  run: any; // FactoryOrderDetails doc (lean, with populated `shipments`)
  order: any; // matching SAPI tracker order (has Key, OrderNumber1, ItemCode, ItemName)
  // Whether the server judged this Run to be the product on the packing-list line.
  // Non-matching Runs are still listed — the picker is the fallback when the name
  // bridge fails — so this marks them rather than hiding them.
  isForItem?: boolean;
  verdict?: {
    level: string;
    cnNode: string | null;
    auNode: string | null;
    matchedSegments: number;
    totalSegments: number;
  };
}

// An item auto-matched to an already-stored ChinaShipmentDetail (by chinaItemCode),
// reusing its existing factoryOrderDetail reference instead of going through MS No lookup.
interface ReusedAssignment {
  reused: true;
  factoryOrderDetail: string;
  old: any; // the existing ChinaShipmentDetail doc
}

// A stock split the user has flagged for a Direct Shipment that doesn't exist yet.
// Nothing is written to China Order Tracker until the mapping is submitted, so
// closing this modal never leaves a stray Run behind.
interface PendingDirectAssignment {
  pendingDirect: true;
  orderInfo: {
    itemCode: string;
    itemName: string;
    picturName: string;
    orderNumber: string;
    key: string;
  };
}

type Assignment = Candidate | ReusedAssignment | PendingDirectAssignment;

// One line of MAPI's answer. The server resolves each split and says what it found,
// so the modal renders a decision rather than making one.
interface ServerLine {
  key: string;
  msNo: string | null;
  status:
    | "RESOLVED"        // exactly one open Run for this item
    | "AMBIGUOUS"       // several share the item code — the operator picks
    | "UNAVAILABLE_ONLY" // Runs exist, but China Order Tracker lists none of them
    | "NO_RUN"          // no Run for this item under this MS No
    | "UNLINKED"        // 口头单, or a blank 澳洲订单号
    | "STOCK_RESOLVED"
    | "STOCK_AMBIGUOUS"
    | "STOCK_NO_DIRECT" // no Direct Shipment yet; creating one is the operator's call
    | "STOCK_UNSCOPED"  // its order split has not resolved, so there is nothing to scope by
    | "LOOKUP_FAILED";
  // The message from a failed tracker lookup, surfaced rather than swallowed.
  detail?: string;
  factoryOrderDetail?: string;
  candidate?: Candidate;
  candidates?: Candidate[];
  expected?: PendingDirectAssignment["orderInfo"];
  nearest?: {
    level: string;
    matchedSegments: number;
    totalSegments: number;
    cnNode: string | null;
    auNode: string | null;
    itemName: string | null;
  } | null;
}

const isReused = (a: Assignment | undefined): a is ReusedAssignment =>
  !!a && (a as ReusedAssignment).reused === true;

const isPendingDirect = (a: Assignment | undefined): a is PendingDirectAssignment =>
  !!a && (a as PendingDirectAssignment).pendingDirect === true;

// A Run the user picked from the candidate table, as opposed to a reused link or a
// Direct Shipment that does not exist yet. Only this kind carries the order line, so
// only this kind can be checked against the item on the packing list.
const isPickedCandidate = (a: Assignment | undefined): a is Candidate =>
  !!a && !isReused(a) && !isPendingDirect(a);

const str = (v: any): string => (v == null ? "" : String(v));

const formatDate = (v: any): string => {
  if (!v) return "";
  const trimmed = String(v).trim();

  // SAPI sends dates as "dd/MM/yyyy" — `new Date(v)` assumes MM/DD/YYYY for
  // slash-separated strings, which silently swaps day/month (or fails outright
  // once the day exceeds 12), so parse this format explicitly.
  const ddmmyyyy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, day, month, year] = ddmmyyyy;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
  }

  const d = new Date(trimmed);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const toNumber = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (v: any): string => {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-US") : String(v);
};

const COL_WIDTHS = { date: 130, qty: 170, label: 96, code: 110, artwork: 110, name: 260 };

const daysBetween = (etd: string, eta: string): number => {
  const ms = new Date(eta).getTime() - new Date(etd).getTime();
  return Math.max(0, Math.round(ms / 86400000));
};

// createFactoryRunShipment's response doesn't return the new shipment's own _id
// directly — it returns the Run(s) sharing that key/orderNumber, each populated
// with its own `shipments` array. Find the one we just added by container number
// and pick the most recently created, so it can be deleted again on rollback.
const findNewestShipmentId = (
  response: any,
  factoryOrderDetailId: string,
  containerNo: string,
): string | null => {
  const runs = response?.data?.factoryOrderData || [];
  const matchingRun = runs.find((r: any) => String(r._id) === String(factoryOrderDetailId));
  const candidates = (matchingRun?.shipments || []).filter(
    (s: any) => String(s.containerNo) === String(containerNo),
  );
  if (!candidates.length) return null;
  candidates.sort(
    (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  return String(candidates[0]._id);
};

// A parsed item's `doc_splits` is the source of truth for how its quantity is
// divided across order references and/or existing stock (e.g. "260251-100" +
// "走库存-5" in the same cell). Older/simple items still come back as a single
// split covering the whole quantity, so this always returns at least one.
const getSplitsForItem = (item: DapiShipmentItem): DapiDocSplit[] => {
  if (item.doc_splits?.length) return item.doc_splits;
  return [
    {
      type: item.from_stock ? "stock" : "order",
      doc_no: item.doc_nos?.[0] ?? null,
      qty: item.total_qty,
    },
  ];
};


// A split only has to be linked to a Run in China Order Tracker when there is an
// order behind it. A 口头单 ("verbal") has none by definition, and neither does a
// line whose 澳洲订单号 cell was simply blank — both are shipped and reported on their
// own. A 走库存 split is NOT exempt: it still links, via a Direct Shipment Run.
const requiresOrderLink = (split: DapiDocSplit): boolean => {
  if (split.type === "verbal") return false;
  if (split.type === "stock") return true;
  return !!split.doc_no;
};


const rowKey = (itemIdx: number, splitIdx: number): string => `${itemIdx}:${splitIdx}`;



const ChinaShipmentMsNoMatchModal: React.FC<Props> = ({
  visible,
  data,
  existingShipment,
  batchPosition,
  onSubmitted,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const toast = useToast();

  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  // rowKey -> what MAPI resolved that line to. The modal no longer works out which
  // Run a line belongs to; it renders the answer and collects the operator's choice
  // where the server could not decide alone.
  const [serverByKey, setServerByKey] = useState<Record<string, ServerLine>>({});
  // "itemIdx:splitIdx" -> chosen candidate (or an auto-matched existing item, in replace mode)
  const [assignments, setAssignments] = useState<Record<string, Assignment>>({});
  // "itemIdx:splitIdx" -> manual search text (used for splits with no automatic match)
  const [manualSearch, setManualSearch] = useState<Record<string, string>>({});
  const [manualSearching, setManualSearching] = useState<Record<string, boolean>>({});
  // The 外箱标 the operator is looking at full size. Held as the data URI itself
  // rather than an index, so it survives the list re-rendering underneath it.
  const [enlargedLabel, setEnlargedLabel] = useState<string | null>(null);

  // The AU label for each candidate, so the factory's carton label can be compared
  // with what Multisteps has on file. Keyed on PicturName, which is the S3 key SAP
  // gives for it.
  const [auLabels, setAuLabels] = useState<Record<string, string>>({});
  // Which items are folded away. Tracked as collapsed rather than expanded so the
  // empty default is "all open", and an item that arrives later opens with the rest
  // instead of needing an effect to keep a set of indices in step with the data.
  const [collapsedItems, setCollapsedItems] = useState<Set<number>>(new Set());
  // Which names have been asked for. The dedupe has to live here rather than in a
  // check on auLabels, because fetchSignedLabelURL answers TWICE for one name: it
  // publishes a no-image placeholder immediately and overwrites it if the real
  // object turns out to exist. Guarding on "already has a value" would keep the
  // placeholder and throw the label away.
  const requestedLabels = useRef<Set<string>>(new Set());
  // Labels of the lines whose MS No has no Run, once the operator has been warned and
  // is being asked to confirm. Null while there is nothing to confirm.
  const [noRunPending, setNoRunPending] = useState<string[] | null>(null);
  // "itemIdx:splitIdx" -> the user has un-ticked "create a Direct Shipment" for this
  // stock split. Tracked explicitly so the default-on effect doesn't re-tick it.
  const [directOptOut, setDirectOptOut] = useState<Record<string, boolean>>({});

  // Finds every already-stored ChinaShipmentDetail this parsed item could correspond
  // to (replace mode only), matched by chinaItemCode — the one field both sides share.
  // Only meaningful for single-split items: chinaItemCode can't tell which stored
  // record a given split belongs to once an item has more than one, so those always
  // fall through to full per-split matching instead of auto-reuse.
  const getOldMatches = (item: DapiShipmentItem): any[] => {
    if (!existingShipment?.items?.length) return [];
    if (getSplitsForItem(item).length > 1) return [];
    const code = String(item.cn_item_code || "").trim();
    if (!code) return [];
    return existingShipment.items.filter(
      (d: any) =>
        String(d.chinaItemCode || "").trim() === code &&
        // The whole point of an old match is to reuse its factoryOrderDetail. A row
        // stored without one (a line with no 澳洲订单号) has nothing to reuse, and
        // matching it would build an assignment whose id is the string "null".
        d.factoryOrderDetail != null,
    );
  };

  // The already-stored ChinaShipmentDetail that a split's chosen Run is currently
  // linked to (replace mode only). Unlike getOldMatches this keys off the resolved
  // Run rather than chinaItemCode, so it also covers multi-split items — which is
  // the only way a 走库存 split's previously-shipped qty can be found, since those
  // items are excluded from chinaItemCode reuse.
  const getOldDetailForRun = (factoryOrderDetailId: string | null): any | null => {
    if (!factoryOrderDetailId || !existingShipment?.items?.length) return null;
    return (
      existingShipment.items.find(
        (d: any) => String(d.factoryOrderDetail) === factoryOrderDetailId,
      ) || null
    );
  };

  // The factoryOrderDetail (Run) id an assignment currently points to, regardless
  // of whether it's a reused default or a manually picked candidate.
  const assignmentFactoryOrderDetailId = (a: Assignment | undefined): string | null => {
    if (!a) return null;
    // A pending Direct Shipment has no Run yet — it gets one at submit time.
    if (isPendingDirect(a)) return null;
    return isReused(a) ? a.factoryOrderDetail : String(a.run._id);
  };


  // A stock split ("走库存") represents extra qty of the SAME product this item's
  // order split already resolved to — never a different product. Once that order
  // split has been matched (via a *fresh* candidate — a reused replace-mode match
  // has no live SAPI order object to read Key/OrderNumber1 from), this gives back
  // everything needed to both scope the stock split's Direct Shipment candidates to
  // the exact same item, and to create a new Direct Shipment for it if none exists.

  // Everything the render needs about a split, read from what MAPI resolved rather
  // than worked out here. `candidates` is what the operator may choose between —
  // empty unless the server left the choice open.
  const getSplitMatchContext = (
    itemIdx: number,
    item: DapiShipmentItem,
    splitIdx: number,
    split: DapiDocSplit,
  ) => {
    const key = rowKey(itemIdx, splitIdx);
    const isStock = split.type === "stock";
    const line = serverByKey[key] || null;
    const status = line?.status ?? null;

    return {
      key,
      line,
      status,
      isStock,
      msNo: line?.msNo ?? (split.doc_no ? String(split.doc_no) : null),
      effectiveMsNo: line?.msNo ?? manualSearch[key]?.trim() ?? null,
      candidates: line?.candidates ?? [],
      // A stock split with no Direct Shipment yet comes back with the order line one
      // would be created against; the decision itself stays the operator's.
      expectedOrderInfo: line?.expected ?? null,
      // "Does a Run exist for this item" — not "is one selectable". An item whose
      // Runs are all closed, cancelled or unapproved still has one, and needs a
      // different answer from one that has none at all, so UNAVAILABLE_ONLY counts.
      hasRunForItem:
        status === "RESOLVED" ||
        status === "AMBIGUOUS" ||
        status === "UNAVAILABLE_ONLY",
    };
  };


  useEffect(() => {
    if (!visible || !data) return;

    setError("");
    setManualSearch({});
    setManualSearching({});
    setServerByKey({});
    setDirectOptOut({});

    // Auto-match single-split items to already-stored ChinaShipmentDetail records
    // (replace mode only) by chinaItemCode. A single line item can have more than
    // one Run, so this defaults to the first one found ("Run 1") — still reusing
    // its factoryOrderDetail directly rather than a fresh MS No lookup — but when
    // there's more than one, the candidate table below is also shown so the user
    // can switch to a different Run.
    const initialAssignments: Record<string, Assignment> = {};
    data.items.forEach((item, itemIdx) => {
      const oldMatches = getOldMatches(item);
      if (oldMatches.length > 0) {
        initialAssignments[rowKey(itemIdx, 0)] = {
          reused: true,
          factoryOrderDetail: String(oldMatches[0].factoryOrderDetail),
          old: oldMatches[0],
        };
      }
    });
    setAssignments(initialAssignments);

    const run = async () => {
      setLoading(true);
      try {
        // One request resolves every line. This used to force a SAP re-query and then
        // issue one round-trip per SAP order line, in sequence — six of them for
        // TESTING_04 alone, because 260341 spans five order lines. Freshness is the
        // server's problem now, so the forced refetch goes with it.
        const response = await matchShipmentItems(
          String(data.container_no || ""),
          data.items,
        );
        const resolved: Record<string, ServerLine> = {};
        for (const line of (response?.data?.lines || []) as ServerLine[]) {
          resolved[line.key] = line;
        }
        setServerByKey(resolved);

        // Anything the server settled on outright. Replace-mode reuse takes priority
        // and is left untouched — a line already tied to a stored shipment detail
        // keeps that tie rather than being re-decided.
        const settled: Record<string, Assignment> = {};
        for (const line of Object.values(resolved)) {
          if (initialAssignments[line.key] || !line.candidate) continue;
          if (line.status !== "RESOLVED" && line.status !== "STOCK_RESOLVED") continue;
          settled[line.key] = line.candidate;
        }
        if (Object.keys(settled).length > 0) {
          setAssignments((prev) => ({ ...prev, ...settled }));
        }
      } catch (err: any) {
        setError(err?.message || err || t("containerLookupFailedError"));
      } finally {
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, data, existingShipment]);

  const handleAssign = (key: string, candidate: Assignment | null) => {
    // Any pick invalidates the pending confirmation — it was raised against the
    // assignments as they stood, and those have just changed.
    setNoRunPending(null);
    setAssignments((prev) => {
      const next = { ...prev };
      if (candidate) {
        next[key] = candidate;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  // Searching another MS No re-resolves that one line, through the same endpoint
  // and therefore the same rules — an override rather than a second code path.
  const handleManualSearch = async (key: string) => {
    const msNo = manualSearch[key]?.trim();
    if (!msNo || !data) return;
    setManualSearching((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await matchShipmentItems(
        String(data.container_no || ""),
        data.items,
        { [key]: msNo },
      );
      const next: Record<string, ServerLine> = {};
      for (const line of (response?.data?.lines || []) as ServerLine[]) {
        next[line.key] = line;
      }
      setServerByKey(next);
    } finally {
      setManualSearching((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Fetches the AU label for every candidate on screen. PicturName is SAP's own key
  // for it, so unlike a lookup by CN item code these resolve. Each distinct name is
  // asked for once; a name that has no label in S3 simply stays absent and the cell
  // shows nothing, which is the honest answer for an item whose artwork is not filed.
  useEffect(() => {
    if (!visible) return;
    const names = new Set<string>();
    for (const line of Object.values(serverByKey)) {
      for (const candidate of line.candidates ?? []) {
        const name = candidate?.order?.PicturName;
        if (name) names.add(String(name));
      }
    }
    for (const name of names) {
      if (requestedLabels.current.has(name)) continue;
      requestedLabels.current.add(name);
      fetchSignedLabelURL(
        name,
        () => {},
        (url: string) => setAuLabels((prev) => ({ ...prev, [name]: url })),
      );
    }
  }, [visible, serverByKey]);

  // The server resolves stock splits; what stays here is the one part it cannot
  // decide — whether to create a Direct Shipment that does not exist yet. Ticked by
  // default, because leaving it unticked silently drops the qty on submit, and left
  // alone once the operator has cleared it.
  useEffect(() => {
    if (!visible || !data) return;

    const additions: Record<string, Assignment> = {};
    for (const line of Object.values(serverByKey)) {
      if (line.status !== "STOCK_NO_DIRECT" || !line.expected) continue;
      if (assignments[line.key] || directOptOut[line.key]) continue;
      additions[line.key] = { pendingDirect: true, orderInfo: line.expected };
    }

    // Only write when something changed — otherwise this retriggers off its own update.
    if (Object.keys(additions).length > 0) {
      setAssignments((prev) => ({ ...prev, ...additions }));
    }
  }, [visible, data, assignments, serverByKey, directOptOut]);

  // Ticking records the intent to create a Direct Shipment at submit time; clearing
  // it drops that intent, leaving the stock qty for manual matching instead.
  const toggleDirectShipment = (
    key: string,
    orderInfo: {
      itemCode: string;
      itemName: string;
      picturName: string;
      orderNumber: string;
      key: string;
    },
  ) => {
    setError("");
    if (isPendingDirect(assignments[key])) {
      handleAssign(key, null);
      setDirectOptOut((prev) => ({ ...prev, [key]: true }));
      return;
    }
    setDirectOptOut((prev) => ({ ...prev, [key]: false }));
    handleAssign(key, { pendingDirect: true, orderInfo });
  };

  // Normalizes a split's carton/qty/weight fields. When the split covers the item's
  // whole quantity (the common, non-split case) this reproduces the original parsed
  // values exactly. For a genuine partial split, per-carton rates (a property of the
  // packaging) stay constant while cartons/totals are derived from this split's own
  // share of the quantity.
  const deriveSplitValues = (item: DapiShipmentItem, split: DapiDocSplit) => {
    // A "misc" one-off line (equipment, moulds, consumables) has no per-carton rates
    // to spread its totals across, so it is treated as a single unbroken unit like
    // film is — its crate count would otherwise divide a real gross weight by rates
    // that do not exist, reporting qtyTotal 0 and gwPerCarton 0.
    const isFilm =
      item.item_type === "film" || item.item_type === "misc" || !item.boxes;
    const itemTotalQty = toNumber(item.total_qty);
    const splitQty = toNumber(split.qty ?? itemTotalQty);
    const isWholeItem = splitQty === itemTotalQty;

    const base = {
      chinaItemCode: str(item.cn_item_code),
      itemDescription: str(item.cn_item_name),
      material: str(item.material),
      spec: str(item.spec),
      label: str(item.feature),
      notes: str(item.remarks),
    };

    if (isWholeItem) {
      const cartons = isFilm ? 1 : toNumber(item.boxes);
      return {
        ...base,
        qtyPerCarton: isFilm ? itemTotalQty : toNumber(item.pcs_per_box),
        qtyCartons: cartons,
        qtyTotal: itemTotalQty,
        gwPerCarton: isFilm ? toNumber(item.total_gross_kg) : toNumber(item.gross_per_box),
        gwCartons: cartons,
        gwTotal: toNumber(item.total_gross_kg),
        nwPerCarton: isFilm ? toNumber(item.total_net_kg) : toNumber(item.net_per_box),
        nwCartons: cartons,
        nwTotal: toNumber(item.total_net_kg),
      };
    }

    const pcsPerCarton = toNumber(item.pcs_per_box);
    const grossPerBox = toNumber(item.gross_per_box);
    const netPerBox = toNumber(item.net_per_box);
    const cartons = isFilm ? 1 : Math.max(1, Math.round(splitQty / (pcsPerCarton || 1)));
    const shareRatio = itemTotalQty > 0 ? splitQty / itemTotalQty : 0;
    const gwTotal = isFilm ? toNumber(item.total_gross_kg) * shareRatio : cartons * grossPerBox;
    const nwTotal = isFilm ? toNumber(item.total_net_kg) * shareRatio : cartons * netPerBox;

    return {
      ...base,
      qtyPerCarton: isFilm ? splitQty : pcsPerCarton,
      qtyCartons: cartons,
      qtyTotal: splitQty,
      gwPerCarton: isFilm ? gwTotal : grossPerBox,
      gwCartons: cartons,
      gwTotal,
      nwPerCarton: isFilm ? nwTotal : netPerBox,
      nwCartons: cartons,
      nwTotal,
    };
  };

  // A line that states a count and the unit it is counted in — 1 个, 8 箱, 10 板 —
  // needs both recorded, because the nine carton fields cannot express either. Film
  // lines have them as much as one-off lines do: a film line ships on pallets and
  // says so, and leaving them out lost 板 counts exactly as it lost 个 counts.
  //
  // Net weight differs between the two, though. A one-off line states none at all,
  // so it is sent as undefined — absent means not stated, where 0 would claim a
  // gearbox weighs nothing. A film line does state its net, on its 毛重 row, so it
  // keeps whatever deriveSplitValues read.
  const packagingFields = (item: DapiShipmentItem) => {
    if (!item.unit && item.pack_count == null) return {};
    const fields: Record<string, unknown> = {
      unit: str(item.unit),
      packCount: toNumber(item.pack_count),
    };
    if (item.item_type === "misc") {
      fields.nwPerCarton = undefined;
      fields.nwTotal = undefined;
    }
    return fields;
  };

  const toShipmentDetailPayload = (
    item: DapiShipmentItem,
    split: DapiDocSplit,
    assignment: Assignment,
  ) => {
    const values = deriveSplitValues(item, split);
    // Recorded on the line itself so stock qty is queryable without joining
    // factory_order_details to check the Run's directShipment flag.
    const fromStock = split.type === "stock";

    // Pending Direct Shipments are converted to a reused assignment during submit,
    // once their Run exists — they never reach here unresolved.
    if (isPendingDirect(assignment)) {
      throw new Error("Direct Shipment has not been created yet");
    }

    if (isReused(assignment)) {
      return {
        factoryOrderDetail: assignment.factoryOrderDetail,
        itemCode: str(assignment.old.itemCode),
        itemName: str(assignment.old.itemName),
        picturName: str(assignment.old.picturName),
        orderNumber: str(assignment.old.orderNumber),
        fromStock,
        itemType: str(item.item_type),
        ...values,
        ...packagingFields(item),
      };
    }

    return {
      factoryOrderDetail: assignment.run._id,
      itemCode: str(assignment.order.ItemCode),
      itemName: str(assignment.order.ItemName),
      picturName: str(assignment.order.PicturName),
      orderNumber: str(assignment.run.orderNumber),
      fromStock,
      itemType: str(item.item_type),
      ...values,
      ...packagingFields(item),
    };
  };

  // A line with no order behind it is stored with no factoryOrderDetail and no Run.
  // There is no matched SAP order to read an itemCode from, so that stays blank; the
  // 口头单 marker is kept as the orderNumber so the report shows why the line is
  // unlinked rather than just showing an empty cell.
  const toUnlinkedShipmentDetailPayload = (
    item: DapiShipmentItem,
    split: DapiDocSplit,
  ) => ({
    factoryOrderDetail: null,
    itemCode: "",
    itemName: str(item.cn_item_name),
    picturName: "",
    // Whatever the 澳洲订单号 cell said, kept even though nothing is linked — for a
    // 口头单 that is the label, and for a confirmed no-Run line it is the only record
    // of which MS No the packing list cited. Null for a blank cell or a stock split.
    orderNumber: str(split.doc_no),
    fromStock: false,
    itemType: str(item.item_type),
    ...deriveSplitValues(item, split),
    ...packagingFields(item),
  });

  // Field-by-field diff between an existing ChinaShipmentDetail and the freshly
  // parsed item it was auto-matched to (by chinaItemCode) — used to show the
  // user exactly what a "replace" would change before they confirm.
  const computeItemChanges = (
    oldItem: any,
    item: DapiShipmentItem,
    split: DapiDocSplit,
  ): { label: string; oldVal: string; newVal: string }[] => {
    const values = deriveSplitValues(item, split);
    const fields: { label: string; key: keyof typeof values }[] = [
      { label: t("itemDescription"), key: "itemDescription" },
      { label: t("materialExcel"), key: "material" },
      { label: t("specExcel"), key: "spec" },
      { label: t("featureExcel"), key: "label" },
      { label: t("pcsPerBoxLabel"), key: "qtyPerCarton" },
      { label: t("boxesLabel"), key: "qtyCartons" },
      { label: t("totalQtyLabel"), key: "qtyTotal" },
      { label: t("grossPerBoxLabel"), key: "gwPerCarton" },
      { label: t("totalGrossKgLabel"), key: "gwTotal" },
      { label: t("netPerBoxLabel"), key: "nwPerCarton" },
      { label: t("totalNetKgLabel"), key: "nwTotal" },
      { label: t("remarksLabel"), key: "notes" },
    ];

    return fields
      .map(({ label, key }) => ({
        label,
        oldVal: str(oldItem?.[key]),
        newVal: str(values[key]),
      }))
      .filter(({ oldVal, newVal }) => oldVal !== newVal);
  };

  // `confirmNoRun` is the operator answering the warning below: their MS No has no
  // Run to ship against, and they have chosen to store the line unlinked anyway.
  const handleSubmit = async (confirmNoRun = false) => {
    if (!data) return;
    const matchedKeys = Object.keys(assignments);
    // Rows that need no Run at all. Collected up front so a packing list made up
    // entirely of unlinked lines can still be submitted.
    const unlinkedRows: { item: DapiShipmentItem; split: DapiDocSplit }[] = [];
    // Order splits needing a Run but left without one. They used to be skipped
    // silently here, which dropped the line from the shipment and took its weight
    // out of the container totals with it — most easily hit now that a closed order
    // line is no longer offered as a match. A cleared stock tick is excluded: its
    // own hint says the qty will not be shipped, so that one is a deliberate choice.
    const unresolvedRows: string[] = [];
    // An open Run for this item exists and simply has not been chosen. Kept apart from
    // the closed-order case because the two need opposite answers: pick the line that
    // is right there, versus there being no line that can take a shipment at all.
    const unpickedRows: string[] = [];
    // Lines the server could not answer for, because the tracker lookup itself failed.
    const lookupFailedRows: string[] = [];
    let lookupFailedDetail: string | null = null;
    // An MS No with no Run at all is a warning rather than a refusal: there is nothing
    // in China Order Tracker to ship against yet, and that is the planner's to fix, so
    // holding the whole container hostage helps nobody. The operator is told, and may
    // store the line unlinked — which at least keeps its weight in the container.
    const noRunRows: { item: DapiShipmentItem; split: DapiDocSplit; label: string }[] = [];
    data.items.forEach((item, itemIdx) => {
      getSplitsForItem(item).forEach((split, splitIdx) => {
        const assigned = assignments[rowKey(itemIdx, splitIdx)];
        if (requiresOrderLink(split)) {
          if (!assigned && split.type === "order") {
            const name = str(item.cn_item_code) || str(item.cn_item_name) || "—";
            const label = `${name} (MS No ${str(split.doc_no)})`;
            // Judged for THIS item, not for the MS No as a whole. An MS No spans
            // several products, and a Run belonging to a different one is no use to
            // this line — which is exactly the case where nobody created a Run for
            // the item actually shipped. Closed Runs are still counted here, so an
            // item whose only Run is closed reports that instead. Direct Shipment
            // Runs never count: they are not a match for an order split.
            const status = serverByKey[rowKey(itemIdx, splitIdx)]?.status;
            if (status === "RESOLVED" || status === "AMBIGUOUS") unpickedRows.push(label);
            else if (status === "UNAVAILABLE_ONLY") unresolvedRows.push(label);
            // A failed lookup is not the same as "there is no Run". Treating it as one
            // would offer to store the line unlinked because SAP happened to be down,
            // which is exactly the wrong answer to a question we could not ask.
            else if (status === "LOOKUP_FAILED") {
              lookupFailedRows.push(label);
              const detail = serverByKey[rowKey(itemIdx, splitIdx)]?.detail;
              if (detail) lookupFailedDetail = detail;
            }
            else noRunRows.push({ item, split, label });
          }
          return;
        }
        if (assigned) return;
        unlinkedRows.push({ item, split });
      });
    });

    // Checked first: nothing else that follows can be trusted when the lookup failed.
    if (lookupFailedRows.length > 0) {
      setError(
        lookupFailedDetail
          ? `${t("containerLookupFailedError")} (${lookupFailedDetail})`
          : t("containerLookupFailedError"),
      );
      return;
    }

    if (unpickedRows.length > 0) {
      setError(
        t("unpickedOrderLineError")
          .replace("{container}", str(data.container_no) || "—")
          .replace("{items}", unpickedRows.join(", ")),
      );
      return;
    }

    // Checked before the no-Run warning: a closed line is refused outright, so there
    // is no point asking the operator to confirm a submit that cannot go through.
    if (unresolvedRows.length > 0) {
      setError(
        t("unresolvedOrderLinesError")
          .replace("{container}", str(data.container_no) || "—")
          .replace("{items}", unresolvedRows.join(", ")),
      );
      return;
    }

    if (noRunRows.length > 0) {
      if (!confirmNoRun) {
        toast.show(
          t("msNoHasNoRunToast").replace("{count}", String(noRunRows.length)),
          { type: "warning" },
        );
        setNoRunPending(noRunRows.map((r) => r.label));
        return;
      }
      // Confirmed. Stored unlinked, so the line and its weight stay in the container
      // rather than being dropped for want of a Run that does not exist yet.
      noRunRows.forEach(({ item, split }) => unlinkedRows.push({ item, split }));
    }

    if (matchedKeys.length === 0 && unlinkedRows.length === 0) {
      setError(t("noItemsMatchedError"));
      return;
    }

    setSubmitting(true);
    setError("");

    const shipmentItems: any[] = [];
    // Every FactoryRunShipmentDetails created during this attempt — rolled back in
    // full if anything at all fails, so a failed submit never leaves a shipment
    // half-created (no more "partial success, some items skipped").
    const createdShipmentIds: string[] = [];
    // Direct Shipment Runs created during this attempt. Deleting a shipment never
    // removes its Run, so these are torn down separately to avoid leaving an empty
    // Run behind on China Order Tracker.
    const createdDirectRunIds: string[] = [];

    const rollback = async () => {
      for (const id of createdShipmentIds) {
        try {
          await dispatch(deleteFactoryRunShipment(id)).unwrap();
        } catch {
          // Best-effort — the error already shown to the user takes priority;
          // an orphaned Run-shipment link here is a rarer, separate failure mode.
        }
      }
      for (const id of createdDirectRunIds) {
        try {
          await dispatch(deleteFactoryOrderDetail(id)).unwrap();
        } catch {
          // Same as above — best-effort cleanup.
        }
      }
    };

    // Runs already linked to this container — whether auto-defaulted or manually
    // re-picked to a Run already part of this shipment — must never go through
    // createFactoryRunShipment again, that would create a duplicate assignment.
    const existingFactoryOrderDetailIds = new Set(
      (existingShipment?.items || [])
        .filter((d: any) => d.factoryOrderDetail != null)
        .map((d: any) => String(d.factoryOrderDetail)),
    );

    for (const key of matchedKeys) {
      const [itemIdxStr, splitIdxStr] = key.split(":");
      const item = data.items[Number(itemIdxStr)];
      const split = getSplitsForItem(item)[Number(splitIdxStr)];
      let assignment = assignments[key];

      // Deferred Direct Shipment: create the Run now. It comes back with its first
      // shipment already attached, so it skips createFactoryRunShipment below.
      if (isPendingDirect(assignment)) {
        const { orderInfo } = assignment;
        try {
          const result = await dispatch(
            createDirectFactoryRunShipment({
              key: orderInfo.key,
              orderNumber: orderInfo.orderNumber,
              shipmentQty: toNumber(split.qty ?? item.total_qty),
              containerNo: data.container_no,
              shipmentNo: 1,
              ETD: data.departure_date,
              ETA: data.arrival_date,
              duration: daysBetween(data.departure_date as any, data.arrival_date as any),
              remarks: "",
            }),
          ).unwrap();

          const newRun = result?.data;
          const newShipment = newRun?.shipments?.[0];
          if (!newRun?._id || !newShipment?._id) {
            throw new Error("Unexpected response creating Direct Shipment");
          }

          createdDirectRunIds.push(String(newRun._id));
          createdShipmentIds.push(String(newShipment._id));

          assignment = {
            reused: true,
            factoryOrderDetail: String(newRun._id),
            old: {
              itemCode: orderInfo.itemCode,
              itemName: orderInfo.itemName,
              picturName: orderInfo.picturName,
              orderNumber: orderInfo.orderNumber,
            },
          };
        } catch {
          await rollback();
          setSubmitting(false);
          setError(t("createDirectShipmentFailedError"));
          return;
        }

        shipmentItems.push(toShipmentDetailPayload(item, split, assignment));
        continue;
      }

      const factoryOrderDetailId = assignmentFactoryOrderDetailId(assignment);
      const alreadyLinkedToThisContainer =
        !!factoryOrderDetailId && existingFactoryOrderDetailIds.has(factoryOrderDetailId);

      if (alreadyLinkedToThisContainer) {
        shipmentItems.push(toShipmentDetailPayload(item, split, assignment));
        continue;
      }

      try {
        const created = await dispatch(
          createFactoryRunShipment({
            factoryOrderDetail: factoryOrderDetailId,
            containerNo: data.container_no,
            shipmentQty: toNumber(split.qty ?? item.total_qty),
            shipmentNo: 1,
            ETD: data.departure_date,
            ETA: data.arrival_date,
            duration: daysBetween(data.departure_date as any, data.arrival_date as any),
            remarks: str(item.remarks),
          }),
        ).unwrap();

        const newId = findNewestShipmentId(created, factoryOrderDetailId as string, data.container_no);
        if (newId) createdShipmentIds.push(newId);

        shipmentItems.push(toShipmentDetailPayload(item, split, assignment));
      } catch (err: any) {
        await rollback();
        setSubmitting(false);
        setError(t("shipmentCreateFailedError"));
        return;
      }
    }

    // Unlinked lines create no Run and call no endpoint, so they are appended after
    // the matched rows and need no rollback handling of their own.
    for (const { item, split } of unlinkedRows) {
      shipmentItems.push(toUnlinkedShipmentDetailPayload(item, split));
    }

    const payload = {
      containerNo: data.container_no,
      sealNo: data.seal,
      vesselVoyage: data.vessel,
      ETD: data.departure_date,
      ETA: data.arrival_date,
      shippingLine: data.shipping_line,
      location: data.destination,
      containerType: data.container_type,
      forwarder: data.forwarder,
      freight: data.freight,
      freeDays: data.free_days,
      vgmKg: data.vgm_kg,
      responsiblePerson: data.responsible_person,
      reportDate: data.report_date,
      remarks: data.remarks,
      sourceFileName: data.source_file,
      items: shipmentItems,
    };

    const result = existingShipment
      ? await dispatch(
          updateGeneratedChinaShipment({ _id: existingShipment._id, payload }),
        )
      : await dispatch(generateChinaShipment(payload));

    if (result.error) {
      await rollback();
      setSubmitting(false);
      const fallbackError = existingShipment
        ? t("shipmentUpdateFailedError")
        : t("shipmentCreateFailedError");
      setError(result.payload || result.error.message || fallbackError);
      return;
    }

    setSubmitting(false);
    const successMsg = existingShipment
      ? t("shipmentUpdatedSuccess")
      : t("shipmentCreatedSuccess");
    toast.show(
      batchPosition
        ? `${successMsg} (${batchPosition.index}/${batchPosition.total})`
        : successMsg,
      { type: "success" },
    );

    dispatch(
      getChinaShipments({
        page: 1,
        pageSize: 10,
        sort: "-createdAt",
        status: "",
        search: "",
      }),
    );

    // In a batch the parent decides what happens next: advance to the next
    // container, or close once this was the last one.
    if (onSubmitted) {
      setError("");
      onSubmitted();
      return;
    }

    handleClose();
  };

  const toggleItemCollapsed = (itemIdx: number) =>
    setCollapsedItems((prev) => {
      const next = new Set(prev);
      next.has(itemIdx) ? next.delete(itemIdx) : next.add(itemIdx);
      return next;
    });

  const expandAllItems = () => setCollapsedItems(new Set());
  const collapseAllItems = () =>
    setCollapsedItems(new Set((data?.items ?? []).map((_: any, i: number) => i)));

  const handleClose = () => {
    setError("");
    setAssignments({});
    setManualSearch({});
    setServerByKey({});
    setDirectOptOut({});
    setNoRunPending(null);
    setEnlargedLabel(null);
    // Signed URLs expire within the hour, so they are not kept for a later open —
    // and the request set has to be cleared with them, or the next open would skip
    // the fetch and show nothing.
    setAuLabels({});
    requestedLabels.current.clear();
    onClose();
  };

  if (!data) return null;

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modal}>
          <View style={styles.titleRow}>
            <Text style={styles.title}>
              {existingShipment ? t("updateShipmentBtn") : t("matchMsNoTitle")}
              {batchPosition
                ? `  ·  ${t("containerProgressLabel")} ${batchPosition.index} / ${batchPosition.total}`
                : ""}
            </Text>
            <TouchableOpacity onPress={handleClose} style={styles.closeIcon}>
              <MaterialCommunityIcons name="close" size={20} color="#555" />
            </TouchableOpacity>
          </View>

          <View style={styles.subtitleRow}>
            <Text style={styles.subtitle}>
              {t("containerNo")}: {data.container_no}
            </Text>
            {data.items.length > 1 && (
              <View style={styles.foldControls}>
                <TouchableOpacity onPress={expandAllItems} style={styles.foldBtn}>
                  <MaterialCommunityIcons name="unfold-more-horizontal" size={14} color="#475569" />
                  <Text style={styles.foldBtnText}>{t("expandAllBtn")}</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={collapseAllItems} style={styles.foldBtn}>
                  <MaterialCommunityIcons name="unfold-less-horizontal" size={14} color="#475569" />
                  <Text style={styles.foldBtnText}>{t("collapseAllBtn")}</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>

          {loading ? (
            <View style={styles.loadingArea}>
              <ActivityIndicator size="large" color="#4dabf7" />
            </View>
          ) : (
            <>
              {error !== "" && <Text style={styles.errorText}>{error}</Text>}

              <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>
                {data.items.map((item, itemIdx) => {
                  const splits = getSplitsForItem(item);
                  const title =
                    [item.cn_item_name, item.cn_item_code].filter(Boolean).join(" | ") ||
                    `Item ${itemIdx + 1}`;
                  const isCollapsed = collapsedItems.has(itemIdx);

                  return (
                    <View key={itemIdx} style={styles.itemRow}>
                      <TouchableOpacity
                        style={styles.itemTitleRow}
                        onPress={() => toggleItemCollapsed(itemIdx)}
                        activeOpacity={0.7}
                      >
                        <MaterialCommunityIcons
                          name={isCollapsed ? "chevron-right" : "chevron-down"}
                          size={18}
                          color="#475569"
                        />
                        <Text style={styles.itemTitle} numberOfLines={1}>{title}</Text>
                      </TouchableOpacity>

                      {!isCollapsed && (<>

                      {/* The factory's carton label for this item. Two runs can share
                          an item code and differ only by customer — the packing list
                          says nothing about which, but the label does, so it is shown
                          where the choice is made. Absent for a bit over half of all
                          items, which is why there is no placeholder. */}
                      {!!item.label_images?.length && (
                        <View style={styles.labelStrip}>
                          {item.label_images.map((img: any, imgIdx: number) => (
                            <TouchableOpacity
                              key={imgIdx}
                              onPress={() => setEnlargedLabel(img.data)}
                              accessibilityLabel={t("cartonLabelLabel")}
                            >
                              <Image
                                source={{ uri: img.data }}
                                style={styles.labelThumb}
                                resizeMode="contain"
                              />
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}

                      {splits.map((split, splitIdx) => {
                        const key = rowKey(itemIdx, splitIdx);

                        // Nothing to match: no 澳洲订单号 behind this line. Rendered as a
                        // plain statement so it reads as handled rather than as a row
                        // the user forgot to link — it is still saved to the shipment.
                        if (!requiresOrderLink(split)) {
                          return (
                            <View
                              key={key}
                              style={[
                                styles.splitBlock,
                                splits.length > 1 && styles.splitBlockBordered,
                              ]}
                            >
                              <View style={styles.itemInfoRow}>
                                <Text style={styles.itemMsNo}>
                                  {split.type === "verbal"
                                    ? t("verbalOrderLabel")
                                    : `${t("msRef")}: —`}
                                  {split.qty != null ? ` · ${formatNumber(split.qty)}` : ""}
                                </Text>
                                <View style={styles.noLinkBadge}>
                                  <MaterialCommunityIcons
                                    name="link-variant-off"
                                    size={12}
                                    color="#92400e"
                                  />
                                  <Text style={styles.noLinkBadgeText}>
                                    {t("noOrderLinkRequiredLabel")}
                                  </Text>
                                </View>
                              </View>
                              <Text style={styles.noLinkExplainer}>
                                {t("noOrderLinkExplainer")}
                              </Text>
                            </View>
                          );
                        }

                        const {
                          line,
                          isStock,
                          msNo,
                          effectiveMsNo,
                          candidates,
                          expectedOrderInfo,
                          hasRunForItem,
                          status,
                        } = getSplitMatchContext(itemIdx, item, splitIdx, split);

                        const assigned = assignments[key];
                        // A pick whose order line is for another product. The table is
                        // deliberately not filtered by item — it is the fallback when
                        // the name bridge fails — so this says "you have crossed
                        // products" without preventing it.
                        // A pick the server judged to be another product. Choosing one
                        // stays possible — the table is the fallback when the name
                        // bridge fails — so this says "you have crossed products"
                        // rather than preventing it.
                        const pickedOtherProduct =
                          !isStock &&
                          isPickedCandidate(assigned) &&
                          assigned.isForItem === false;
                        const pickedVerdict = pickedOtherProduct
                          ? (assigned as Candidate).verdict ?? null
                          : null;
                        // Several Runs carry this exact item code. Not a failure of the
                        // comparison — the AU catalogue really does list one code for
                        // different customers or base types, and the packing list does
                        // not say which. Nothing is auto-selected, so say why.
                        const ambiguousIdentity =
                          !isStock && !assigned && status === "AMBIGUOUS";
                        const oldMatches = splits.length === 1 ? getOldMatches(item) : [];
                        const isAmbiguousOld = oldMatches.length > 1;
                        const selectedFactoryOrderDetailId = assignmentFactoryOrderDetailId(assigned);
                        const matchedOldItem = selectedFactoryOrderDetailId
                          ? oldMatches.find(
                              (o) => String(o.factoryOrderDetail) === selectedFactoryOrderDetailId,
                            ) || null
                          : null;
                        // Multi-split items never populate oldMatches, so a 走库存 split
                        // would otherwise show no old→new comparison at all when its
                        // qty changes between uploads.
                        const diffOldItem =
                          matchedOldItem || getOldDetailForRun(selectedFactoryOrderDetailId);
                        const showPicker = isAmbiguousOld || oldMatches.length === 0;
                        const hasNoMatch =
                          oldMatches.length === 0 && !!effectiveMsNo && candidates.length === 0 && !assigned;
                        const searchedButEmptyForStock =
                          isStock &&
                          !!effectiveMsNo &&
                          candidates.length === 0 &&
                          (!assigned || isPendingDirect(assigned));

                        return (
                          <View
                            key={key}
                            style={[
                              styles.splitBlock,
                              splits.length > 1 && styles.splitBlockBordered,
                              hasNoMatch && styles.itemRowNoMatch,
                            ]}
                          >
                            {splits.length > 1 && (
                              <Text style={styles.splitLabel}>
                                {isStock
                                  ? t("stockSplitLabel")
                                  : `${t("orderSplitLabel")} ${split.doc_no ?? ""}`}
                                {split.qty != null ? ` · ${formatNumber(split.qty)}` : ""}
                              </Text>
                            )}

                            <View style={styles.itemInfoRow}>
                              <Text style={styles.itemMsNo}>
                                {t("msRef")}: {str(msNo) || "—"}
                                {/* A lone split gets no ORDER header above it, since that
                                    would only repeat the MS No on this line. Its quantity
                                    still has to appear somewhere, so it goes here — the
                                    way an unlinked line already shows it. Without this the
                                    card names no quantity at all and reads as if one were
                                    missing. */}
                                {splits.length === 1 && split.qty != null
                                  ? ` · ${formatNumber(split.qty)}`
                                  : ""}
                              </Text>
                              {matchedOldItem && (
                                <View style={styles.matchedBadge}>
                                  <MaterialCommunityIcons name="link-variant" size={12} color="#0f766e" />
                                  <Text style={styles.matchedBadgeText}>{t("matchedToExistingLabel")}</Text>
                                </View>
                              )}
                              {pickedOtherProduct ? (
                                <View style={styles.crossPickChip}>
                                  <MaterialCommunityIcons name="alert-outline" size={12} color="#b45309" />
                                  <Text style={styles.crossPickChipText}>
                                    {pickedVerdict?.level === "SAME_FAMILY"
                                      ? t("skuSameFamilyLabel")
                                          .replace("{cn}", str(pickedVerdict.cnNode) || "—")
                                          .replace("{au}", str(pickedVerdict.auNode) || "—")
                                      : t("differentProductPickedLabel")}
                                  </Text>
                                </View>
                              ) : ambiguousIdentity ? (
                                <View style={styles.crossPickChip}>
                                  <MaterialCommunityIcons name="alert-outline" size={12} color="#b45309" />
                                  <Text style={styles.crossPickChipText}>
                                    {t("skuAmbiguousLabel")}
                                  </Text>
                                </View>
                              ) : !isStock && !hasRunForItem ? (
                                <View style={styles.noRunChip}>
                                  <MaterialCommunityIcons name="information-outline" size={12} color="#6b7280" />
                                  <Text style={styles.noRunChipText}>
                                    {t("noRunForItemLabel")}
                                    {/* The nearest miss, when there was one. "CN 125C2
                                        vs AU 125C9" tells the operator far more than
                                        the bare fact that nothing matched. */}
                                    {line?.nearest
                                      ? ` · ${t("skuSameFamilyLabel")
                                          .replace("{cn}", str(line.nearest.cnNode) || "—")
                                          .replace("{au}", str(line.nearest.auNode) || "—")}`
                                      : ""}
                                  </Text>
                                </View>
                              ) : null}
                            </View>

                            {diffOldItem && (() => {
                              const changes = computeItemChanges(diffOldItem, item, split);
                              return changes.length > 0 ? (
                                <View style={styles.diffBox}>
                                  {changes.map((c, cIdx) => (
                                    <View key={cIdx} style={styles.diffRow}>
                                      <Text style={styles.diffLabel}>{c.label}</Text>
                                      <View style={styles.diffValues}>
                                        <Text style={styles.diffOld}>{c.oldVal || "—"}</Text>
                                        <MaterialCommunityIcons name="arrow-right" size={12} color="#94a3b8" />
                                        <Text style={styles.diffNew}>{c.newVal || "—"}</Text>
                                      </View>
                                    </View>
                                  ))}
                                </View>
                              ) : (
                                <Text style={styles.noChangesText}>{t("noChangesLabel")}</Text>
                              );
                            })()}

                            {isAmbiguousOld && (
                              <Text style={styles.otherRunsHint}>{t("otherRunsAvailableLabel")}</Text>
                            )}

                            {showPicker && (candidates.length > 0 ? (
                              <ScrollView horizontal showsHorizontalScrollIndicator style={styles.tableScroll}>
                                <View style={styles.table}>
                                  <View style={styles.tableHeaderRow}>
                                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.date }]}>{t("sapRequestDateLabel")}</Text>
                                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.qty }]}>{t("shippedQtyLabel")}</Text>
                                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.label }]}>{t("auLabelLabel")}</Text>
                                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.code }]}>{t("itemCode")}</Text>
                                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.artwork }]}>{t("artworkNoLabel")}</Text>
                                    <Text style={[styles.tableHeaderCell, { width: COL_WIDTHS.name }]}>{t("itemName")}</Text>
                                  </View>

                                  <ScrollView style={styles.tableBodyScroll} showsVerticalScrollIndicator>
                                    {candidates.map((c, cIdx) => {
                                      const isSelected = selectedFactoryOrderDetailId === String(c.run._id);
                                      const requestDate = formatDate(c.order.RequestDate) || "—";
                                      const shippedQty = (c.run.shipments || []).reduce(
                                        (sum: number, s: any) => sum + toNumber(s.shipmentQty),
                                        0,
                                      );
                                      const plannedQty = c.run.plannedQty;
                                      const auLabel = c.order.PicturName
                                        ? auLabels[String(c.order.PicturName)]
                                        : undefined;
                                      return (
                                        <TouchableOpacity
                                          key={`${c.run._id}-${cIdx}`}
                                          // Tapping the highlighted row clears it, so a
                                          // pick can be undone — what New Item used to
                                          // do, without a control that reads as already
                                          // chosen. Stock splits are excluded: the
                                          // effect above re-selects a lone Direct
                                          // Shipment the moment it is unassigned, so the
                                          // row would snap straight back. Their opt-out
                                          // is the Direct Shipment checkbox.
                                          onPress={() =>
                                            handleAssign(key, !isStock && isSelected ? null : c)
                                          }
                                          style={[styles.tableRow, isSelected && styles.tableRowSelected]}
                                        >
                                          <Text
                                            style={[styles.tableCellStrong, { width: COL_WIDTHS.date }, isSelected && styles.tableCellSelected]}
                                          >
                                            {requestDate}
                                          </Text>
                                          <Text
                                            style={[styles.tableCellStrong, { width: COL_WIDTHS.qty }, isSelected && styles.tableCellSelected]}
                                          >
                                            {formatNumber(shippedQty)}/{formatNumber(plannedQty)}
                                          </Text>
                                          <View style={{ width: COL_WIDTHS.label, paddingHorizontal: 4 }}>
                                            {auLabel ? (
                                              <TouchableOpacity onPress={() => setEnlargedLabel(auLabel)}>
                                                <Image
                                                  source={{ uri: auLabel }}
                                                  style={styles.auLabelThumb}
                                                  resizeMode="contain"
                                                />
                                              </TouchableOpacity>
                                            ) : (
                                              <Text
                                                style={[styles.tableCell, { width: COL_WIDTHS.label }, isSelected && styles.tableCellSelected]}
                                              >
                                                —
                                              </Text>
                                            )}
                                          </View>
                                          <Text
                                            style={[styles.tableCell, { width: COL_WIDTHS.code }, isSelected && styles.tableCellSelected]}
                                            numberOfLines={1}
                                          >
                                            {c.order.ItemCode || "—"}
                                          </Text>
                                          <Text
                                            style={[styles.tableCell, { width: COL_WIDTHS.artwork }, isSelected && styles.tableCellSelected]}
                                            numberOfLines={1}
                                          >
                                            {c.order.ArtworkNo || "—"}
                                          </Text>
                                          <Text
                                            style={[styles.tableCell, { width: COL_WIDTHS.name }, isSelected && styles.tableCellSelected]}
                                            numberOfLines={2}
                                          >
                                            {c.order.ItemName || "—"}
                                          </Text>
                                        </TouchableOpacity>
                                      );
                                    })}
                                  </ScrollView>
                                </View>
                              </ScrollView>
                            ) : (
                              <View>
                                <Text style={styles.newItemBadge}>
                                  {searchedButEmptyForStock ? t("noDirectShipmentLabel") : t("noMatchLabel")}
                                </Text>
                                {isStock && expectedOrderInfo && (
                                  <View
                                    style={[
                                      styles.createDirectShipmentBox,
                                      !isPendingDirect(assigned) && styles.directShipmentBoxCleared,
                                    ]}
                                  >
                                    <TouchableOpacity
                                      style={styles.directShipmentCheckRow}
                                      onPress={() => toggleDirectShipment(key, expectedOrderInfo)}
                                      activeOpacity={0.7}
                                    >
                                      <MaterialCommunityIcons
                                        name={
                                          isPendingDirect(assigned)
                                            ? "checkbox-marked"
                                            : "checkbox-blank-outline"
                                        }
                                        size={20}
                                        color={isPendingDirect(assigned) ? "#2563EB" : "#94A3B8"}
                                      />
                                      <Text style={styles.directShipmentCheckLabel}>
                                        {t("createDirectShipmentCheckbox")} {expectedOrderInfo.itemCode}{" "}
                                        · {formatNumber(split.qty)}
                                      </Text>
                                    </TouchableOpacity>

                                    <Text
                                      style={[
                                        styles.directShipmentConsequence,
                                        !isPendingDirect(assigned) &&
                                          styles.directShipmentConsequenceCleared,
                                      ]}
                                    >
                                      {isPendingDirect(assigned)
                                        ? t("directShipmentTickedHint")
                                        : t("directShipmentClearedHint")}
                                    </Text>
                                  </View>
                                )}
                                <View style={styles.manualSearchRow}>
                                  <TextInput
                                    style={styles.manualSearchInput}
                                    value={manualSearch[key] ?? ""}
                                    onChangeText={(v) => setManualSearch((prev) => ({ ...prev, [key]: v }))}
                                    placeholder={t("msRef")}
                                    placeholderTextColor="#bbb"
                                  />
                                  <TouchableOpacity
                                    style={styles.manualSearchBtn}
                                    onPress={() => handleManualSearch(key)}
                                    disabled={!!manualSearching[key]}
                                  >
                                    {manualSearching[key] ? (
                                      <ActivityIndicator size="small" color="#374151" />
                                    ) : (
                                      <Text style={styles.manualSearchBtnText}>{t("searchBtn")}</Text>
                                    )}
                                  </TouchableOpacity>
                                </View>
                              </View>
                            ))}
                          </View>
                        );
                      })}
                      </>)}
                    </View>
                  );
                })}

                {existingShipment && (() => {
                  // Based on what's actually currently selected across all items — not
                  // just whether the item code still appears somewhere — so a line item
                  // with multiple Runs correctly shows the *unselected* one(s) as removed.
                  const selectedFactoryOrderDetailIds = new Set(
                    Object.values(assignments)
                      .map((a) => assignmentFactoryOrderDetailId(a))
                      .filter(Boolean),
                  );
                  const removedItems = (existingShipment.items || []).filter(
                    (oldItem: any) =>
                      !selectedFactoryOrderDetailIds.has(String(oldItem.factoryOrderDetail)),
                  );
                  return removedItems.length > 0 ? (
                    <View style={styles.removedSection}>
                      <Text style={styles.removedSectionTitle}>
                        {t("itemsToBeRemovedLabel")}
                      </Text>
                      {removedItems.map((oldItem: any, i: number) => (
                        <Text key={i} style={styles.removedRowText}>
                          {oldItem.itemDescription || oldItem.chinaItemCode} ({oldItem.chinaItemCode})
                        </Text>
                      ))}
                    </View>
                  ) : null;
                })()}

                <View style={{ height: 24 }} />
              </ScrollView>
            </>
          )}

          {noRunPending && noRunPending.length > 0 && (
            <View style={styles.warnBar}>
              <Text style={styles.warnBarText}>
                {t("msNoHasNoRunWarning").replace("{items}", noRunPending.join(", "))}
              </Text>
              <View style={styles.warnBarActions}>
                <TouchableOpacity
                  style={styles.warnCancelBtn}
                  onPress={() => setNoRunPending(null)}
                >
                  <Text style={styles.warnCancelText}>{t("cancel")}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.warnConfirmBtn}
                  onPress={() => {
                    setNoRunPending(null);
                    void handleSubmit(true);
                  }}
                >
                  <Text style={styles.warnConfirmText}>{t("createAnywayBtn")}</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}

          {/* Tapping a carton label fills the card with it — the detail that decides
              which customer a line is for is often small print, and a thumbnail is
              not enough to read it. Tapping anywhere dismisses. */}
          {enlargedLabel && (
            <TouchableOpacity
              style={styles.labelOverlay}
              activeOpacity={1}
              onPress={() => setEnlargedLabel(null)}
              accessibilityLabel={t("close")}
            >
              <Image
                source={{ uri: enlargedLabel }}
                style={styles.labelFull}
                resizeMode="contain"
              />
            </TouchableOpacity>
          )}

          <View style={styles.footer}>
            <TouchableOpacity style={styles.closeBtn} onPress={handleClose}>
              <Text style={styles.closeBtnText}>{t("cancel")}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.submitBtn, submitting && styles.btnDisabled]}
              // Wrapped, not passed directly: onPress hands the press event to its
              // handler, which would arrive as a truthy confirmNoRun.
              onPress={() => void handleSubmit()}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.submitBtnText}>
                  {existingShipment ? t("updateShipmentBtn") : t("createShipmentBtn")}
                </Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ChinaShipmentMsNoMatchModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.4)",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    width: "90%",
    maxWidth: 900,
    maxHeight: "85%",
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 20,
  },
  titleRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111",
  },
  closeIcon: {
    padding: 4,
  },
  subtitle: {
    fontSize: 13,
    color: "#555",
    marginBottom: 14,
  },
  loadingArea: {
    paddingVertical: 40,
    alignItems: "center",
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 13,
    marginBottom: 12,
  },
  warnBar: {
    backgroundColor: "#fffbeb",
    borderWidth: 1,
    borderColor: "#fcd34d",
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  warnBarText: {
    color: "#92400e",
    fontSize: 13,
    lineHeight: 19,
  },
  warnBarActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 10,
  },
  warnCancelBtn: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 8,
    paddingVertical: 7,
    paddingHorizontal: 16,
    backgroundColor: "#fff",
  },
  warnCancelText: {
    color: "#374151",
    fontSize: 13,
    fontWeight: "600",
  },
  warnConfirmBtn: {
    borderRadius: 8,
    paddingVertical: 7,
    paddingHorizontal: 16,
    backgroundColor: "#b45309",
  },
  warnConfirmText: {
    color: "#fff",
    fontSize: 13,
    fontWeight: "700",
  },
  scroll: {
    maxHeight: 440,
  },
  itemRow: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    backgroundColor: "#fff",
    gap: 8,
  },
  itemRowNoMatch: {
    borderColor: "#fbbf24",
    backgroundColor: "#fffbeb",
  },
  splitBlock: {
    gap: 6,
  },
  splitBlockBordered: {
    borderTopWidth: 1,
    borderTopColor: "#f1f5f9",
    paddingTop: 8,
    marginTop: 2,
  },
  splitLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  itemInfoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  itemInfo: {
    flex: 1,
  },
  labelStrip: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 6,
    marginBottom: 2,
  },
  subtitleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 8,
  },
  foldControls: {
    flexDirection: "row",
    gap: 6,
  },
  foldBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    backgroundColor: "#f8fafc",
  },
  foldBtnText: {
    fontSize: 11,
    color: "#475569",
  },
  itemTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  auLabelThumb: {
    width: 88,
    height: 40,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    backgroundColor: "#fff",
  },
  labelThumb: {
    width: 108,
    height: 48,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    backgroundColor: "#fff",
  },
  labelOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(15,23,42,0.88)",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 10,
  },
  labelFull: {
    width: "100%",
    height: "100%",
  },
  itemTitle: {
    fontSize: 13,
    fontWeight: "600",
    color: "#111",
  },
  itemMsNo: {
    fontSize: 12,
    color: "#555",
  },
  newItemBadge: {
    fontSize: 11,
    color: "#b45309",
    fontWeight: "700",
    marginBottom: 6,
  },
  tableScroll: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
  },
  table: {
    overflow: "hidden",
  },
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f1f5f9",
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
  },
  tableHeaderCell: {
    fontSize: 10,
    fontWeight: "700",
    color: "#6b7280",
    textTransform: "uppercase",
  },
  tableBodyScroll: {
    maxHeight: 220,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f5f9",
    backgroundColor: "#fff",
  },
  tableRowSelected: {
    backgroundColor: "#61B8FF",
  },
  tableCell: {
    fontSize: 12,
    color: "#6b7280",
    paddingRight: 8,
  },
  tableCellStrong: {
    fontSize: 12,
    fontWeight: "700",
    color: "#111827",
    paddingRight: 8,
  },
  tableCellSelected: {
    color: "#fff",
  },
  createDirectShipmentBox: {
    borderWidth: 1,
    borderColor: "#bfdbfe",
    backgroundColor: "#eff6ff",
    borderRadius: 8,
    padding: 10,
    gap: 8,
    marginBottom: 8,
  },
  directShipmentBoxCleared: {
    backgroundColor: "#F8FAFC",
    borderColor: "#DDE1E8",
  },
  directShipmentCheckRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  directShipmentCheckLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: "600",
    color: "#1A2B4A",
  },
  directShipmentConsequence: {
    fontSize: 12,
    color: "#2563EB",
    marginTop: 6,
    marginLeft: 28,
  },
  directShipmentConsequenceCleared: {
    color: "#94A3B8",
  },
  manualSearchRow: {
    flexDirection: "row",
    gap: 8,
  },
  manualSearchInput: {
    flex: 1,
    height: 34,
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 13,
    backgroundColor: "#fafafa",
  },
  manualSearchBtn: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 6,
    paddingHorizontal: 14,
    justifyContent: "center",
    backgroundColor: "#fff",
  },
  manualSearchBtnText: {
    fontSize: 13,
    fontWeight: "600",
    color: "#374151",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    paddingTop: 14,
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
  },
  closeBtn: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: "#fff",
  },
  closeBtnText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#374151",
  },
  submitBtn: {
    backgroundColor: "#61B8FF",
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  submitBtnText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#fff",
  },
  btnDisabled: {
    opacity: 0.6,
  },
  matchedBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#ecfdf5",
    borderWidth: 1,
    borderColor: "#a7f3d0",
  },
  crossPickChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#fffbeb",
    borderWidth: 1,
    borderColor: "#fcd34d",
    flexShrink: 1,
  },
  crossPickChipText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#b45309",
    flexShrink: 1,
  },
  noRunChip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#f9fafb",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    flexShrink: 1,
  },
  noRunChipText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#6b7280",
    flexShrink: 1,
  },
  matchedBadgeText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#0f766e",
  },
  noLinkBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "#fef3c7",
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  noLinkBadgeText: {
    fontSize: 11,
    color: "#92400e",
    fontWeight: "600",
  },
  noLinkExplainer: {
    fontSize: 11,
    color: "#64748b",
    marginTop: 4,
  },
  otherRunsHint: {
    fontSize: 11,
    color: "#b45309",
    fontStyle: "italic",
    marginTop: 6,
    marginBottom: 4,
  },
  diffBox: {
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 8,
    backgroundColor: "#f8fafc",
    padding: 10,
    gap: 6,
  },
  diffRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
  },
  diffLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: "#6b7280",
    flexShrink: 0,
  },
  diffValues: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexShrink: 1,
  },
  diffOld: {
    fontSize: 12,
    color: "#9ca3af",
    textDecorationLine: "line-through",
  },
  diffNew: {
    fontSize: 12,
    fontWeight: "700",
    color: "#0f766e",
  },
  noChangesText: {
    fontSize: 12,
    color: "#9ca3af",
    fontStyle: "italic",
  },
  removedSection: {
    borderWidth: 1,
    borderColor: "#fecaca",
    backgroundColor: "#fef2f2",
    borderRadius: 10,
    padding: 12,
    marginTop: 4,
    gap: 4,
  },
  removedSectionTitle: {
    fontSize: 12,
    fontWeight: "700",
    color: "#b91c1c",
    marginBottom: 4,
  },
  removedRowText: {
    fontSize: 12,
    color: "#7f1d1d",
  },
});
